import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Search, ShieldCheck } from "lucide-react";
import { PageHeader, Badge, EmptyState } from "@/components/Common";
import ModulePermissionsDialog from "@/components/ModulePermissionsDialog";

const ROLES = [
  "managing_director", "executive_director", "general_manager",
  "branch_manager", "department_head", "supervisor", "staff",
];

export default function Users() {
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const initial = { email: "", password: "", name: "", role: "staff", branch: "", department: "", phone: "" };
  const [form, setForm] = useState(initial);

  const [refreshKey, setRefreshKey] = useState(0);
  const [permissionsUser, setPermissionsUser] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [{ data: u }, { data: b }, { data: d }] = await Promise.all([
          api.get("/users"), api.get("/branches"), api.get("/departments"),
        ]);
        if (!cancelled) { setItems(u); setBranches(b); setDepartments(d); }
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);
  const load = () => setRefreshKey((k) => k + 1);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false);
      setForm(initial);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const filtered = items.filter((i) =>
    !q || `${i.name} ${i.email} ${i.role} ${i.branch}`.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div>
      <PageHeader
        eyebrow="Administration"
        title="Users & Roles"
        subtitle="Manage staff accounts and role-based access."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-emerald-900 hover:bg-emerald-950 text-white"
                data-testid="user-new-button"
              >
                <Plus size={16} className="mr-1.5" /> New user
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display">New user</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="user-form">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Full name</Label>
                    <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="mt-1" data-testid="user-name-input" />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input required type="email" value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="mt-1" data-testid="user-email-input" />
                  </div>
                </div>
                <div>
                  <Label>Password</Label>
                  <Input required type="password" minLength={6}
                    value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="mt-1" data-testid="user-password-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Role</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger className="mt-1" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ROLES.map((r) => (<SelectItem key={r} value={r}>{r.replaceAll("_", " ")}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="mt-1" data-testid="user-phone-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Branch</Label>
                    <Select value={form.branch} onValueChange={(v) => setForm({ ...form, branch: v })}>
                      <SelectTrigger className="mt-1" data-testid="user-branch-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
                      <SelectContent>
                        {branches.map((b) => (<SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Department</Label>
                    <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                      <SelectTrigger className="mt-1" data-testid="user-department-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
                      <SelectContent>
                        {departments.map((d) => (<SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={busy}
                    className="bg-emerald-900 hover:bg-emerald-950 text-white"
                    data-testid="user-submit-button">
                    {busy ? "Creating…" : "Create user"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="bg-white border border-gray-200 rounded-md p-3 mb-4 flex gap-3 items-center">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search users…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 h-9"
            data-testid="user-search-input" />
        </div>
        <span className="text-xs text-gray-500">{filtered.length} user(s)</span>
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <EmptyState title="No users found" hint="Add the first user above." />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="users-table">
              <thead className="bg-gray-50">
                <tr>
                  {["Name", "Email", "Role", "Branch", "Department", "Phone", "Access", ""].map((h) => (
                    <th key={h} className="text-left text-xs font-bold text-gray-600 uppercase tracking-wider px-4 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{u.name}</td>
                    <td className="px-4 py-3 text-gray-700">{u.email}</td>
                    <td className="px-4 py-3"><Badge tone="emerald">{u.role?.replaceAll("_", " ")}</Badge></td>
                    <td className="px-4 py-3 text-gray-700">{u.branch || "—"}</td>
                    <td className="px-4 py-3 text-gray-700">{u.department || "—"}</td>
                    <td className="px-4 py-3 text-gray-700">{u.phone || "—"}</td>
                    <td className="px-4 py-3">
                      {(u.module_access === null || u.module_access === undefined) ? (
                        <Badge tone="emerald">Full access</Badge>
                      ) : (
                        <Badge tone="amber">{(u.module_access || []).length} module(s)</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="sm"
                        onClick={() => setPermissionsUser(u)}
                        className="text-emerald-900 hover:bg-emerald-50 mr-1"
                        data-testid={`user-modules-${u.id}`}>
                        <ShieldCheck size={14} className="mr-1" /> Modules
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(u.id)}
                        className="text-red-700 hover:bg-red-50"
                        data-testid={`user-delete-${u.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ModulePermissionsDialog
        user={permissionsUser}
        open={!!permissionsUser}
        onClose={() => setPermissionsUser(null)}
        onSaved={load}
      />
    </div>
  );
}
